import { Component, DestroyRef, OnInit, ViewChild, inject, ChangeDetectionStrategy, DOCUMENT } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { getMessaging, getToken, onMessage, isSupported, Messaging } from "firebase/messaging";
import { environment } from "../../../../environments/environment";
import { AuthService } from "../../../services/auth.service";
import { Notification } from "../../../entities/notification";
import { MatBadge } from "@angular/material/badge";
import { MatIcon } from "@angular/material/icon";

import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { MatIconButton } from "@angular/material/button";
import { MitpManagerService } from "../../../services/mitp-manager.service";
import { NotificationsService } from "../../../services/notifications.service";
import { PushNotification } from "../../../entities/push-notification";
import { MatDivider } from "@angular/material/divider";
import { EMPTY, Subject, catchError, filter, first, fromEvent, interval, merge, switchMap } from "rxjs";
import { NgxTolgeeModule } from "@tolgee/ngx";
import { Router } from "@angular/router";
import { ENotificationType } from "../../../enums/enotification-type";

/** How often the bell asks for new notifications while the tab is in front. */
export const NOTIFICATIONS_REFRESH_MS = 2 * 60 * 1000;

@Component({
	selector: "app-notifications-view",
	templateUrl: "./notifications-view.component.html",
	styleUrls: ["./notifications-view.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [MatIconButton, MatMenuTrigger, MatIcon, MatBadge, MatMenu, MatMenuItem, MatDivider, NgxTolgeeModule],
})
export class NotificationsViewComponent implements OnInit {
	authService = inject(AuthService);
	private manager = inject(MitpManagerService);
	private notificationService = inject(NotificationsService);
	private router = inject(Router);
	private document = inject(DOCUMENT);
	private destroyRef = inject(DestroyRef);
	private refresh$ = new Subject<void>();
	private messagingSetupComplete = false;
	private messagingInstance: Promise<Messaging | null> | null = null;

	isEnabled = false;
	messages: Notification[] = [];
	readonly NotificationType = ENotificationType;
	@ViewChild("menuTrigger") menu: MatMenuTrigger;

	ngOnInit() {
		this.watchNotifications();
		if (this.authService.isAuth()) {
			this.initMessaging();
			this.readNotifications();
		}
		this.authService.authStatus.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status) => {
			if (status) {
				this.initMessaging();
				this.readNotifications();
			}
		});
	}

	readNotifications(): void {
		this.refresh$.next();
	}

	/**
	 * Reloads on demand, every two minutes while the tab is in front, and when the user comes back
	 * to it: a bounce report reaches the API minutes after the send, with no push to announce it.
	 */
	private watchNotifications(): void {
		const isVisible = () => this.document.visibilityState === "visible";
		merge(
			this.refresh$,
			interval(NOTIFICATIONS_REFRESH_MS).pipe(filter(isVisible)),
			fromEvent(this.document, "visibilitychange").pipe(filter(isVisible))
		)
			.pipe(
				filter(() => this.authService.isAuth()),
				switchMap(() => this.notificationService.getNotifications().pipe(catchError(() => EMPTY))),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((notifications) => {
				this.messages = notifications;
			});
	}

	private initMessaging(): void {
		if (this.messagingSetupComplete) {
			return;
		}

		this.getMessagingIfSupported()
			.then((messaging) => {
				if (!messaging) {
					return;
				}

				this.messagingSetupComplete = true;
				this.requestPermission(messaging);
				this.listen(messaging);
			})
			.catch((error) => {
				console.warn("An error occurred while initialising Firebase messaging.", error);
			});
	}

	private getMessagingIfSupported(): Promise<Messaging | null> {
		if (!this.messagingInstance) {
			this.messagingInstance = isSupported()
				.then((supported) => {
					if (!supported) {
						console.warn("Firebase messaging is not supported in this browser.");
						return null;
					}

					return getMessaging();
				})
				.catch((error) => {
					console.warn("Unable to determine Firebase messaging support.", error);
					return null;
				});
		}

		return this.messagingInstance;
	}

	private requestPermission(messaging: Messaging): void {
		getToken(messaging, { vapidKey: environment.firebaseConfig.vapidKey })
			.then((currentToken) => {
				if (currentToken) {
					this.isEnabled = true;
					this.notificationService.registerFcm(currentToken).subscribe();
				} else {
					console.log("No registration token available. Request permission to generate one.");
				}
			})
			.catch((err) => {
				console.log("An error occurred while retrieving token. ", err);
			});
	}

	private listen(messaging: Messaging) {
		onMessage(messaging, (payload) => {
			const message = new PushNotification();
			message.title = payload.notification.title;
			message.body = payload.notification.body;
			// this.messages.unshift(message);
		});
	}

	OnClickItem($event: MouseEvent, item: Notification) {
		$event.stopPropagation();
		this.menu.closeMenu();
		if (!item.isRead) {
			item.isRead = true;
			this.notificationService.readNotification(item.id).pipe(first()).subscribe();
		}
		if (item.link) {
			this.openLink(item.link);
		} else {
			this.manager.openInfoDialog(item.body ?? "");
		}
	}

	/** An app path goes through the router; anything else opens in a new tab. */
	private openLink(link: string): void {
		if (link.startsWith("/")) {
			this.router.navigateByUrl(link);
		} else {
			window.open(link, "_blank", "noopener");
		}
	}

	getBadgeNumber(): number {
		return this.messages.filter((m) => !m.isRead).length;
	}

	showHistory(): void {
		this.router.navigate(["/notifications/history"]);
	}
}
